/**
 * Human-readable byte formatting, shared by Admin + Tenant storage views.
 *
 * Scales B → KB → MB → GB, showing 1–2 fraction digits only for small
 * magnitudes (so "512 MB" not "512.0 MB", but "1.25 GB"). Negative / NaN
 * inputs clamp to 0 B.
 *
 * `locale` (optional) writes the number in that language — «42,1 GB» in
 * Spanish, «1,234 GB» stays «1.234» only where the language says so. Without
 * it the output is exactly what it always was (a dot, no grouping), so the
 * callers that do not pass one see no change.
 */
export function formatBytes(bytes: number, locale?: string): string {
  const n = Math.max(0, Number.isFinite(bytes) ? bytes : 0);
  const fixed = (value: number, digits: number): string => {
    if (locale === undefined) return value.toFixed(digits);
    try {
      return value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    } catch {
      return value.toFixed(digits);
    }
  };
  if (n < 1024) return `${fixed(Math.round(n), 0)} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${fixed(kb, kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${fixed(mb, mb < 10 ? 1 : 0)} MB`;
  const gb = mb / 1024;
  return `${fixed(gb, gb < 10 ? 2 : 1)} GB`;
}

/** GB → bytes (1 GiB). Mirrors the backend's ``max_storage_gb * 1024**3``. */
export function gbToBytes(gb: number): number {
  return gb * 1024 ** 3;
}
